import { z } from 'zod';
import {
  SubscriptionInterval,
  SubscriptionPerk,
  SubscriptionSource,
  SubscriptionStatus,
  SubscriptionTier,
} from '../enums.js';
import { IsoCurrencySchema, IsoDateTimeSchema, UuidSchema } from '../primitives.js';

export const SubscriptionPlanSchema = z.object({
  id: UuidSchema,
  tier: SubscriptionTier,
  interval: SubscriptionInterval,
  creditsPerWeek: z.number().int().min(0),
  perks: z.array(SubscriptionPerk),
  stripeProductId: z.string().nullable(),
  active: z.boolean(),
});
export type SubscriptionPlan = z.infer<typeof SubscriptionPlanSchema>;

export const SubscriptionPlanPriceSchema = z.object({
  planId: UuidSchema,
  currency: IsoCurrencySchema,
  /** Price in currency's minor unit (cents, paise, etc.). */
  amountMinorUnits: z.number().int().min(0),
  stripePriceId: z.string(),
});
export type SubscriptionPlanPrice = z.infer<typeof SubscriptionPlanPriceSchema>;

export const SubscriptionSchema = z.object({
  id: UuidSchema,
  userId: UuidSchema,
  planId: UuidSchema,
  status: SubscriptionStatus,
  source: SubscriptionSource,
  stripeSubscriptionId: z.string().nullable(),
  iapOriginalTransactionId: z.string().nullable(),
  currentPeriodStart: IsoDateTimeSchema,
  currentPeriodEnd: IsoDateTimeSchema,
  cancelAtPeriodEnd: z.boolean(),
  trialEndsAt: IsoDateTimeSchema.nullable(),
  createdAt: IsoDateTimeSchema,
});
export type Subscription = z.infer<typeof SubscriptionSchema>;

/**
 * Entitlements payload — what `billing-service` returns for the current user.
 * Every other service queries this rather than inspecting subscription tables
 * directly. Cached 60s in-process at the billing-service layer (Redis once
 * AWS lands per Phase 0 deferrals); invalidated on subscription state
 * change, ledger writes, trial end, founder flag set, promo start/end.
 *
 * Plan §Monetization Architecture §Entitlements API.
 */
export const ActivePromoEventSchema = z.object({
  id: UuidSchema,
  name: z.string(),
  endsAt: IsoDateTimeSchema,
});
export type ActivePromoEvent = z.infer<typeof ActivePromoEventSchema>;

export const EntitlementsSchema = z.object({
  credits: z.number().int().min(0),
  subscription: SubscriptionSchema.pick({
    id: true,
    status: true,
    source: true,
    currentPeriodEnd: true,
    cancelAtPeriodEnd: true,
    trialEndsAt: true,
  })
    .extend({
      tier: SubscriptionTier,
      interval: SubscriptionInterval,
    })
    .nullable(),
  perks: z.array(SubscriptionPerk),
  /** Number of profile boosts available to spend (e.g. weekly Gold boost). */
  boostCredits: z.number().int().min(0),
  /** Founder cohort flag (first 100K users to pass liveness). Permanent. */
  founder: z.boolean(),
  /**
   * Signup-trial cutoff. Non-null while the user is within the 7-day
   * trial granted at liveness. Founders also have this set but it's
   * redundant — their grant is permanent.
   */
  trialEndsAt: IsoDateTimeSchema.nullable(),
  /**
   * Active "free for all" admin promotional event matching the user's
   * segment, if any. Null when no event is currently active for this
   * user (the typical case at MVP since events are admin-triggered).
   */
  activePromoEvent: ActivePromoEventSchema.nullable(),
});
export type Entitlements = z.infer<typeof EntitlementsSchema>;
